import { useMemo, useState, type ReactNode } from 'react'
import {
  BadgeStatus,
  Button,
  Card,
  CardHeader,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  StatCard,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { AkunKas } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

export default function KasBank() {
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>(FILTER_KOSONG)
  const [detail, setDetail] = useState<AkunKas | null>(null)
  const [modalTransfer, setModalTransfer] = useState(false)

  const daftar = usePaged<AkunKas>('/cash-accounts', {
    query: { unitId: filter.unitId || undefined, q: undefined },
  })

  const kolom = useMemo<KolomTabel<AkunKas>[]>(
    () => [
      { kunci: 'code', judul: 'Kode', render: (b) => <span className="font-medium text-netral-800">{b.code}</span> },
      { kunci: 'name', judul: 'Nama Akun' },
      { kunci: 'unit', judul: 'Unit', render: (b) => b.unit?.name ?? '—' },
      { kunci: 'accountType', judul: 'Jenis', render: (b) => <BadgeStatus status={b.accountType} /> },
      {
        kunci: 'bank',
        judul: 'Bank',
        render: (b) => (
          <div>
            <p>{b.bankName ?? '—'}</p>
            <p className="text-xs text-netral-500">{b.accountNumberMasked ?? ''}</p>
          </div>
        ),
      },
      {
        kunci: 'currentBalance',
        judul: 'Saldo',
        rata: 'kanan',
        render: (b) => (
          <span className={b.currentBalance < 0 ? 'font-semibold text-red-700' : 'font-semibold text-netral-800'}>
            {formatRupiah(b.currentBalance)}
          </span>
        ),
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <Button ukuran="kecil" varian="garis" onClick={() => setDetail(b)}>
            Lihat Saldo
          </Button>
        ),
      },
    ],
    [],
  )

  const totalSaldo = daftar.items.reduce((a, b) => a + (b.currentBalance ?? 0), 0)

  return (
    <div>
      <PageHeader
        judul="Kas dan Bank"
        deskripsi="Daftar akun kas dan bank beserta saldonya. Saldo dihitung dari jurnal yang sah."
        ekspor={{
          path: '/reports/bank-book',
          namaDasar: 'buku-bank',
          query: { unitId: filter.unitId || undefined },
        }}
        aksi={
          punyaIzin('transaction:create') ? (
            <Button onClick={() => setModalTransfer(true)}>+ Transfer Antar Akun</Button>
          ) : null
        }
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ unitId: true, dari: false, sampai: false, fundId: false, program: false, kategori: false }}
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Total Saldo Kas & Bank (halaman ini)"
          nilai={totalSaldo}
          formatNilai={(v) => formatRupiah(Number(v ?? 0))}
        />
        <StatCard label="Jumlah Akun" nilai={daftar.items.length} />
        <StatCard
          label="Akun Defisit"
          nilai={daftar.items.filter((b) => (b.currentBalance ?? 0) < 0).length}
          nada="merah"
        />
      </div>

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada akun kas/bank."
      />

      <ModalSaldo akun={detail} onTutup={() => setDetail(null)} />
      <FormTransfer terbuka={modalTransfer} onTutup={() => setModalTransfer(false)} setelah={daftar.muat} />
    </div>
  )
}

function ModalSaldo({ akun, onTutup }: { akun: AkunKas | null; onTutup: () => void }) {
  const saldo = useApi<unknown>(akun ? `/cash-accounts/${akun.id}/saldo` : null)

  const nilai = useMemo(() => {
    const d = saldo.data
    if (typeof d === 'number') return d
    if (d && typeof d === 'object') {
      const o = d as Record<string, unknown>
      for (const k of ['saldo', 'currentBalance', 'saldoAkhir', 'saldoSaatIni', 'balance']) {
        if (typeof o[k] === 'number') return o[k] as number
      }
    }
    return null
  }, [saldo.data])

  return (
    <Modal
      terbuka={akun !== null}
      onTutup={onTutup}
      judul={akun ? `Saldo ${akun.name}` : 'Saldo'}
      lebar="max-w-xl"
      footer={
        <Button varian="garis" onClick={onTutup}>
          Tutup
        </Button>
      }
    >
      {akun ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Kode Akun">{akun.code}</Info>
            <Info label="Jenis">{akun.accountType}</Info>
            <Info label="Bank">{akun.bankName ?? '—'}</Info>
            <Info label="Nomor Rekening">{akun.accountNumberMasked ?? '—'}</Info>
            <Info label="Saldo Awal">{formatRupiah(akun.openingBalance ?? 0)}</Info>
            <Info label="Saldo Tercatat (cache)">{formatRupiah(akun.currentBalance)}</Info>
          </div>

          <Card padat className="p-3">
            <CardHeader judul="Saldo dari Jurnal" deskripsi="Dihitung langsung dari jurnal sah (sum debit - sum kredit)." />
            {saldo.memuat ? (
              <p className="mt-2 text-sm text-netral-500">Memuat saldo…</p>
            ) : saldo.galat ? (
              <p className="mt-2 text-sm text-red-600">{saldo.galat}</p>
            ) : (
              <p className="angka-tabular mt-2 text-2xl font-semibold text-yayasan-800">
                {nilai === null ? '—' : formatRupiah(nilai)}
              </p>
            )}
          </Card>
        </div>
      ) : null}
    </Modal>
  )
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium tracking-wide text-netral-500 uppercase">{label}</p>
      <p className="mt-0.5 text-sm text-netral-800">{children}</p>
    </div>
  )
}

function FormTransfer({
  terbuka,
  onTutup,
  setelah,
}: {
  terbuka: boolean
  onTutup: () => void
  setelah: () => void
}) {
  const opsiUnit = useOpsiUnit()
  const akunKas = useApi<unknown>(terbuka ? '/cash-accounts' : null, { perPage: 300 })
  const daftarKas: AkunKas[] = Array.isArray(akunKas.data)
    ? (akunKas.data as AkunKas[])
    : ((akunKas.data as { items?: AkunKas[] } | null)?.items ?? [])

  const [unitId, setUnitId] = useState('')
  const [sumber, setSumber] = useState('')
  const [tujuan, setTujuan] = useState('')
  const [jenis, setJenis] = useState('BANK_TO_BANK')
  const [jumlah, setJumlah] = useState('')
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10))
  const [referensi, setReferensi] = useState('')
  const [keterangan, setKeterangan] = useState('')
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    if (!sumber || !tujuan) return setGalat('Akun sumber dan tujuan wajib dipilih.')
    if (sumber === tujuan) return setGalat('Akun sumber dan tujuan tidak boleh sama.')
    const nilai = Number(jumlah.replace(/[^0-9.-]/g, ''))
    if (!nilai || nilai <= 0) return setGalat('Jumlah transfer harus lebih dari nol.')

    setMemuat(true)
    try {
      await api.post(
        '/transactions',
        {
          unitId,
          transactionType: jenis,
          transactionDate: tanggal,
          description: keterangan.trim() || `Transfer antar akun ${jenis}`,
          cashAccountId: sumber,
          destinationCashAccountId: tujuan,
          amount: nilai,
          referenceNumber: referensi.trim() || undefined,
          paymentMethod: 'BANK_TRANSFER',
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelah()
      onTutup()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  const opsiKas = daftarKas.map((k) => ({ value: k.id, label: `${k.code} — ${k.name}` }))

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Transfer Antar Akun Kas/Bank"
      deskripsi="Untuk transfer LINTAS UNIT gunakan menu Transfer Antarunit."
      lebar="max-w-2xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan Transfer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}
        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={opsiUnit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Select
            label="Jenis Transfer"
            opsi={[
              { value: 'BANK_TO_BANK', label: 'Bank ke Bank' },
              { value: 'CASH_TO_BANK', label: 'Kas ke Bank' },
              { value: 'CASH_IN', label: 'Penambahan Kas' },
              { value: 'BANK_IN', label: 'Penambahan Bank' },
            ]}
            value={jenis}
            onChange={(e) => setJenis(e.target.value)}
          />
          <Select
            label="Kas/Bank Sumber"
            placeholder="Pilih akun sumber"
            opsi={opsiKas}
            value={sumber}
            onChange={(e) => setSumber(e.target.value)}
            required
            bantuan={akunKas.galat ? `Gagal memuat akun kas: ${akunKas.galat}` : undefined}
          />
          <Select
            label="Kas/Bank Tujuan"
            placeholder="Pilih akun tujuan"
            opsi={opsiKas}
            value={tujuan}
            onChange={(e) => setTujuan(e.target.value)}
            required
          />
          <Input
            label="Jumlah (Rp)"
            inputMode="numeric"
            value={jumlah}
            onChange={(e) => setJumlah(e.target.value)}
            required
          />
          <Input type="date" label="Tanggal" value={tanggal} onChange={(e) => setTanggal(e.target.value)} required />
          <Input label="Nomor Referensi" value={referensi} onChange={(e) => setReferensi(e.target.value)} />
        </KolomForm>
        <Textarea
          label="Keterangan"
          value={keterangan}
          onChange={(e) => setKeterangan(e.target.value)}
          rows={2}
        />
      </div>
    </Modal>
  )
}
